import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Building2, 
  FileText, 
  Brain, 
  Users, 
  Video, 
  Calendar, 
  CheckCircle, 
  BarChart3,
  ArrowRight
} from 'lucide-react';

const HowItWorksEmployers = () => {
  const { t, language } = useLanguage();

  const steps = [
    {
      icon: Building2,
      title: t('howItWorksEmployers.step1Title'),
      description: t('howItWorksEmployers.step1Desc')
    },
    {
      icon: FileText,
      title: language === 'ar' ? 'نشر الوظائف' : 'Post Vacancies',
      description: language === 'ar' ? 'أنشئ إعلانات وظائف مفصلة مع المتطلبات والمزايا ونطاقات الرواتب.' : 'Create detailed job postings with requirements, benefits, and salary ranges.'
    },
    {
      icon: Brain,
      title: language === 'ar' ? 'مطابقة الذكاء الاصطناعي' : 'AI Matching',
      description: language === 'ar' ? 'يقوم الذكاء الاصطناعي لدينا تلقائياً بتصفية وترتيب المرشحين بناءً على متطلباتك.' : 'Our AI automatically filters and ranks candidates based on your requirements.'
    },
    {
      icon: Users,
      title: language === 'ar' ? 'مراجعة المرشحين' : 'Review Candidates',
      description: language === 'ar' ? 'تصفح المرشحين المطابقين مع أوراق اعتماد موثقة ودرجات AI وملفات فيديو.' : 'Browse matched candidates with verified credentials, AI scores, and video profiles.'
    },
    {
      icon: Video,
      title: language === 'ar' ? 'طلب المقابلات' : 'Request Interviews',
      description: language === 'ar' ? 'ادعُ المرشحين الواعدين للمقابلات بنقرة واحدة.' : 'Invite promising candidates for interviews with one click.'
    },
    {
      icon: Calendar,
      title: language === 'ar' ? 'جدولة تلقائية' : 'Automated Scheduling',
      description: language === 'ar' ? 'يقوم مساعدنا بتنسيق أوقات المقابلات بينك وبين المرشحين تلقائياً.' : 'Our assistant coordinates interview times between you and candidates automatically.'
    },
    {
      icon: CheckCircle,
      title: language === 'ar' ? 'تقديم العروض' : 'Make Offers',
      description: language === 'ar' ? 'أرسل العروض وأدر اتفاقيات العقود من خلال المنصة.' : 'Send offers and manage contract agreements through the platform.'
    },
    {
      icon: BarChart3,
      title: language === 'ar' ? 'تتبع وحلل' : 'Track & Analyse',
      description: language === 'ar' ? 'راقب مقاييس التوظيف ووقت التوظيف والعائد على الاستثمار من خلال تحليلات شاملة.' : 'Monitor recruitment metrics, time-to-hire, and ROI through comprehensive analytics.'
    }
  ];

  const features = [
    {
      title: language === 'ar' ? 'التحقق من المستندات' : 'Document Verification',
      description: language === 'ar' ? 'تحقق آلي من الرخص الطبية والدرجات والشهادات والمراجع مع كشف الاحتيال.' : 'Automated verification of medical licences, degrees, certifications, and references with fraud detection.',
      metrics: language === 'ar' ? 'دقة 99.9%' : '99.9% accuracy'
    },
    {
      title: language === 'ar' ? 'تحليل فيديو بالذكاء الاصطناعي' : 'AI Video Analysis',
      description: language === 'ar' ? 'يكمل كل مرشح مقابلة فيديو يحللها الذكاء الاصطناعي لمهارات التواصل والاحترافية.' : 'Every candidate completes a video interview analysed by AI for communication skills and professionalism.',
      metrics: language === 'ar' ? 'وفر 5 ساعات لكل توظيف' : 'Save 5 hours per hire'
    },
    {
      title: language === 'ar' ? 'ترتيب ذكي' : 'Smart Ranking',
      description: language === 'ar' ? 'يتم ترتيب المرشحين تلقائياً بناءً على المؤهلات والخبرة والتوافق مع متطلباتك.' : 'Candidates are automatically ranked based on qualifications, experience, and fit for your requirements.',
      metrics: language === 'ar' ? 'نجاح المطابقة 95%' : '95% match success'
    },
    {
      title: language === 'ar' ? 'تتبع الامتثال' : 'Compliance Tracking',
      description: language === 'ar' ? 'تتبع ترخيص SCFHS ومعالجة التأشيرات وحالة التأهيل لجميع التعيينات في لوحة تحكم واحدة.' : 'Track SCFHS licensing, visa processing, and onboarding status for all hires in one dashboard.',
      metrics: language === 'ar' ? 'رؤية كاملة' : 'Full visibility'
    },
    {
      title: language === 'ar' ? 'مساعد المقابلات' : 'Interview Assistant',
      description: language === 'ar' ? 'يولد الذكاء الاصطناعي أسئلة مقابلة ذات صلة بناءً على الدور وخلفية المرشح.' : 'AI generates relevant interview questions based on the role and candidate background.',
      metrics: language === 'ar' ? 'تحضير فوري' : 'Instant preparation'
    },
    {
      title: language === 'ar' ? 'تحليلات العائد على الاستثمار' : 'ROI Analytics',
      description: language === 'ar' ? 'تقارير مفصلة عن تكاليف التوظيف ووقت الملء وجودة المرشح وكفاءة التوظيف.' : 'Detailed reports on hiring costs, time-to-fill, candidate quality, and recruitment efficiency.',
      metrics: language === 'ar' ? 'قرارات قائمة على البيانات' : 'Data-driven decisions'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('howItWorksEmployers.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('howItWorksEmployers.subtitle')}
            </p>
          </div>

          {/* Steps */}
          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'ar' ? 'عملية التوظيف في 8 خطوات' : 'Your Recruitment Process in 8 Steps'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <Card key={index} className="relative hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="absolute -top-4 start-6 w-8 h-8 bg-[#42b1c2] text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-lg flex items-center justify-center mb-4 mt-2">
                      <step.icon className="h-6 w-6 text-[#42b1c2]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'ar' ? 'ميزات قوية لتوظيف أفضل' : 'Powerful Features for Better Hiring'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="border-2 hover:border-[#42b1c2] transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="text-sm font-semibold text-[#42b1c2] mb-3">{feature.metrics}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Metrics */}
          <div className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-3xl p-8 md:p-12 mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'ar' ? 'النتائج المتوسطة' : 'Average Results'}
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#42b1c2] mb-2">60%</div>
                <div className="text-gray-700 font-medium">{language === 'ar' ? 'توظيف أسرع' : 'Faster Hiring'}</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#42b1c2] mb-2">{language === 'ar' ? '21 يوم' : '21 days'}</div>
                <div className="text-gray-700 font-medium">{language === 'ar' ? 'متوسط وقت التوظيف' : 'Average Time-to-Hire'}</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#42b1c2] mb-2">95%</div>
                <div className="text-gray-700 font-medium">{language === 'ar' ? 'معدل نجاح المطابقة' : 'Match Success Rate'}</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#42b1c2] mb-2">40%</div>
                <div className="text-gray-700 font-medium">{language === 'ar' ? 'تخفيض التكلفة' : 'Cost Reduction'}</div>
              </div>
            </div>
          </div>

          {/* Comparison */}
          <div className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'ar' ? 'التقليدي مقابل لوما ميد جلوبال' : 'Traditional vs. Luma Med Global'}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                    {language === 'ar' ? 'التوظيف التقليدي' : 'Traditional Recruitment'}
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>• {language === 'ar' ? 'متوسط وقت التوظيف 45-60 يوم' : '45-60 days average time-to-hire'}</li>
                    <li>• {language === 'ar' ? 'فحص يدوي للسير الذاتية والتحقق' : 'Manual CV screening & verification'}</li>
                    <li>• {language === 'ar' ? 'رسوم وكالات توظيف عالية (20-30%)' : 'High recruitment agency fees (20-30%)'}</li>
                    <li>• {language === 'ar' ? 'رؤية محدودة للمرشحين' : 'Limited candidate visibility'}</li>
                    <li>• {language === 'ar' ? 'لا دعم للنقل' : 'No relocation support'}</li>
                    <li>• {language === 'ar' ? 'أدوات متعددة غير متصلة' : 'Multiple disconnected tools'}</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-2 border-[#42b1c2] bg-[#42b1c2]/5">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold text-[#42b1c2] mb-6">Luma Med Global</h3>
                  <ul className="space-y-3 text-gray-700 font-medium">
                    <li className="flex items-start space-x-2 rtl:space-x-reverse">
                      <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span>{language === 'ar' ? 'متوسط وقت التوظيف 21 يوم' : '21 days average time-to-hire'}</span>
                    </li>
                    <li className="flex items-start space-x-2 rtl:space-x-reverse">
                      <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span>{language === 'ar' ? 'فحص وتحقق مدعوم بالذكاء الاصطناعي' : 'AI-powered screening & verification'}</span>
                    </li>
                    <li className="flex items-start space-x-2 rtl:space-x-reverse">
                      <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span>{language === 'ar' ? 'تسعير شفاف (اشتراك أو 15%)' : 'Transparent pricing (subscription or 15%)'}</span>
                    </li>
                    <li className="flex items-start space-x-2 rtl:space-x-reverse">
                      <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span>{language === 'ar' ? 'ملفات فيديو وتحليلات مفصلة' : 'Video profiles & detailed analytics'}</span>
                    </li>
                    <li className="flex items-start space-x-2 rtl:space-x-reverse">
                      <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span>{language === 'ar' ? 'مساعدة نقل كاملة' : 'Complete relocation assistance'}</span>
                    </li>
                    <li className="flex items-start space-x-2 rtl:space-x-reverse">
                      <CheckCircle className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                      <span>{language === 'ar' ? 'منصة توظيف شاملة' : 'All-in-one recruitment platform'}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {language === 'ar' ? 'هل أنت مستعد لتحويل عملية التوظيف لديك؟' : 'Ready to Transform Your Recruitment?'}
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              {language === 'ar' ? 'انضم إلى مرافق الرعاية الصحية السعودية الرائدة التي تستخدم بالفعل لوما ميد جلوبال للعثور على مواهب استثنائية.' : 'Join leading Saudi healthcare facilities already using Luma Med Global to find exceptional talent.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register/employer">
                <Button size="lg" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white px-8 py-6 text-lg" data-testid="employer-start-cta">
                  {t('howItWorksEmployers.registerNow')}
                  <ArrowRight className="ms-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-[#42b1c2] text-[#42b1c2] hover:bg-[#42b1c2] hover:text-white px-8 py-6 text-lg" data-testid="employer-pricing-cta">
                  {language === 'ar' ? 'عرض الأسعار' : 'View Pricing'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HowItWorksEmployers;
